import { useMemo } from 'react';
import { Stack, Grid, Card, CardHeader, CardContent, Box, TextField, Button, Tooltip, Typography } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { CrudFilters, getDefaultFilter } from '@refinedev/core';
import { List, useDataGrid, EditButton, DeleteButton, DateField } from '@refinedev/mui';
import { useModalForm } from '@refinedev/react-hook-form';
import { useForm } from '@refinedev/react-hook-form';
import { Controller } from 'react-hook-form';

import CreatePhaseModal from './components/CreatePhaseModal/CreatePhaseModal';

const PhaseList = () => {
  const createModalFormProps = useModalForm({
    refineCoreProps: { action: 'create' }
  });

  const {
    modal: { show: showCreateModal }
  } = createModalFormProps;
  const { dataGridProps, filters, search } = useDataGrid({
    onSearch: (params: { search: string }) => {
      const filters: CrudFilters = [];

      filters.push({
        field: 'search',
        operator: 'eq',
        value: params.search
      });

      return filters;
    },

    filters: {
      initial: [
        {
          field: 'search',
          operator: 'eq',
          value: ''
        }
      ]
    }
  });

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        width: 200,
        flex: 2,
        renderCell: params => {
          return (
            <Tooltip title={params.row.name}>
              <Typography variant="body1" noWrap>
                {params.row.name}
              </Typography>
            </Tooltip>
          );
        }
      },
      { field: 'description', headerName: 'Description', width: 100, flex: 1 },
      {
        field: 'created',
        headerName: 'Created At',
        renderCell: ({ value }) => <DateField format="LLL" value={value} />,
        flex: 1
      },
      {
        field: 'modified',
        headerName: 'Updated At',
        renderCell: ({ value }) => <DateField format="LLL" value={value} />,
        flex: 1
      },
      {
        field: 'mitigations',
        headerName: 'Mitigations',
        width: 100,
        flex: 1,
        valueFormatter: (value?: Array<any>) => {
          if (value == null) {
            return 0;
          }
          return value.length;
        }
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 250,
        sortable: false,
        renderCell: params => {
          return (
            <Stack direction="row" spacing={1}>
              <EditButton hideText recordItemId={params.row.id} />
              <DeleteButton
                hideText
                recordItemId={params.row.id}
                confirmTitle={'Do you want to delete Phase: "' + params.row.name + '" ?'}
              />
            </Stack>
          );
        }
      }
    ],
    []
  );

  const { control, handleSubmit } = useForm({
    defaultValues: {
      search: getDefaultFilter('search', filters, 'eq') || ''
    }
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={3}>
        <Card>
          <CardHeader title="Filters" />
          <CardContent>
            <Box
              component="form"
              autoComplete="off"
              sx={{
                display: 'flex',
                flexDirection: 'column'
              }}
              onSubmit={handleSubmit(search)}
            >
              <Controller
                name="search"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    label="Search"
                    placeholder="Search by name"
                    margin="normal"
                    fullWidth
                    autoFocus
                  />
                )}
              />

              <br />
              <Button type="submit" variant="contained">
                Apply
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} lg={9}>
        <List
          canCreate
          createButtonProps={{
            onClick: () => showCreateModal()
          }}
        >
          <DataGridPro {...dataGridProps} filterModel={undefined} disableColumnMenu columns={columns} pagination />
          <CreatePhaseModal {...createModalFormProps} />
        </List>
      </Grid>
    </Grid>
  );
};

export default PhaseList;
